import { memo, type RefAttributes } from 'react';
import { faArrowRight } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { router } from '@inertiajs/react';

import { Icon } from '@/components/ui/icon';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import type { TreeItemComponentProps } from '@/components/ui/sortable-tree';
import { TreeItemWrapper } from '@/components/ui/sortable-tree';
import { isScheduleMissed, isSchedulePendingOrInGrace } from '@/lib/page-scheduling';
import { cn } from '@/lib/utils';
import type { Page } from '@/types/page';
import { PageDropdownMenu } from './page-dropdown-menu';
import { PageContent } from './page-item';
import { hasNestedPageView } from './page-utils';
import { useIsPageActive } from './use-is-page-active';

export interface SortablePageItemData {
  page: Page;
  onOpenNestedView?: (page: Page) => void;
  canPin?: boolean;
}

export const SortablePageItem = memo(function SortablePageItem({
  ref,
  ...props
}: TreeItemComponentProps<SortablePageItemData> & RefAttributes<HTMLDivElement>) {
  const { page, onOpenNestedView, canPin } = props.item;
  const { clone, ghost } = props;
  const isActive = useIsPageActive(page.id);
  const isPublished = page.isPublished;
  const hasUnpublishedChanges = page.hasUnpublishedChanges;
  const schedulePending = isSchedulePendingOrInGrace(page.scheduledPublishAt);
  const scheduleMissed = isScheduleMissed(page.scheduledPublishAt);
  const pageType = page.pageType;
  const displayTitle = page.navigationTitle || page.title;
  const hasOwnNestedView = hasNestedPageView(page.pageType);
  const menuClassName = hasOwnNestedView ? 'right-1' : undefined;

  const handleClick = () => {
    if (clone) return;

    if (hasOwnNestedView) {
      onOpenNestedView?.(page);
    }

    if (!page.url) {
      return;
    }

    if (hasOwnNestedView && isActive) {
      return;
    }

    router.visit(page.url);
  };

  return (
    <TreeItemWrapper
      {...props}
      ref={ref}
      showDragHandle={false}
      hideCollapseButton
      className={cn(
        'group/menu-item relative !mb-0',
        !isPublished && 'opacity-50 text-sidebar-foreground/70',
        ghost,
        clone && 'w-64 bg-sidebar shadow-md',
      )}
      contentClassName="!border-0 !p-0"
    >
      <SidebarMenuButton
        isActive={isActive}
        className={cn(
          hasOwnNestedView ? 'pr-10' : 'pr-7',
          page.pinned && 'border-l-2 border-l-primary rounded-l-none bg-sidebar-accent/50',
        )}
        onClick={handleClick}
      >
        <PageContent
          pageType={pageType}
          title={displayTitle}
          isPublished={isPublished}
          hasUnpublishedChanges={hasUnpublishedChanges}
          isScheduled={schedulePending}
          isScheduleMissed={scheduleMissed}
          pinned={page.pinned}
        />
        {hasOwnNestedView && (
          <Icon
            icon={faArrowRight}
            className="ml-auto shrink-0 text-sidebar-foreground/70"
            aria-hidden
          />
        )}
      </SidebarMenuButton>
      {!clone && (
        <PageDropdownMenu page={page} showOnHover canPin={canPin} className={menuClassName} />
      )}
    </TreeItemWrapper>
  );
});
